import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, Download, Search, Eye, Users, DollarSign, 
  Calendar, ChevronDown, ChevronRight, FileText, ArrowUp, ArrowDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatMonto = (monto) => {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(monto || 0);
};

const formatFecha = (fecha) => {
  if (!fecha) return '-';
  const parts = fecha.split('-');
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return fecha;
};

export default function MorosidadSection({ clubId, categorias, onVerCartola }) {
  const [morosos, setMorosos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalDeuda, setTotalDeuda] = useState(0);
  const [totalMorosos, setTotalMorosos] = useState(0);
  const [totalCuotas, setTotalCuotas] = useState(0);
  const [fechaCorte, setFechaCorte] = useState(() => {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
  });
  const [categoriaFiltro, setCategoriaFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [exportando, setExportando] = useState(false);
  const [ordenarPor, setOrdenarPor] = useState('monto');
  const [ordenDir, setOrdenDir] = useState('desc');

  const cargarMorosidad = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('fecha_corte', fechaCorte);
      if (categoriaFiltro !== 'todos') params.append('categoria_id', categoriaFiltro);
      
      const res = await axios.get(`${API}/clubes-deportivos/${clubId}/reporte-morosidad?${params}`, { withCredentials: true });
      setMorosos(res.data.morosos || []);
      setTotalDeuda(res.data.total_deuda || 0);
      setTotalMorosos(res.data.total_morosos || 0);
      setTotalCuotas(res.data.total_cuotas_impagas || 0);
    } catch (error) {
      toast.error('Error al cargar reporte de morosidad');
    } finally {
      setLoading(false);
    }
  }, [clubId, fechaCorte, categoriaFiltro]);

  useEffect(() => {
    cargarMorosidad();
  }, [cargarMorosidad]);

  const handleExportExcel = async () => {
    setExportando(true);
    try {
      const params = new URLSearchParams();
      params.append('fecha_corte', fechaCorte);
      if (categoriaFiltro !== 'todos') params.append('categoria_id', categoriaFiltro);
      
      const res = await axios.get(`${API}/clubes-deportivos/${clubId}/reporte-morosidad/exportar?${params}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `morosidad_${fechaCorte}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExportando(false);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleOrden = (campo) => {
    if (ordenarPor === campo) {
      setOrdenDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setOrdenarPor(campo);
      setOrdenDir(campo === 'nombre' ? 'asc' : 'desc');
    }
  };

  const morososFiltrados = (() => {
    let lista = busqueda.trim()
      ? morosos.filter(m => {
          const nombre = `${m.integrante?.nombre || ''} ${m.integrante?.apellido || ''}`.toLowerCase();
          const rut = (m.integrante?.rut || '').toLowerCase();
          const q = busqueda.toLowerCase();
          return nombre.includes(q) || rut.includes(q);
        })
      : [...morosos];
    
    lista.sort((a, b) => {
      let valA, valB;
      if (ordenarPor === 'nombre') {
        valA = `${a.integrante?.apellido || ''} ${a.integrante?.nombre || ''}`.toLowerCase();
        valB = `${b.integrante?.apellido || ''} ${b.integrante?.nombre || ''}`.toLowerCase();
        return ordenDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      } else if (ordenarPor === 'cuotas') {
        valA = a.cuotas_impagas || 0;
        valB = b.cuotas_impagas || 0;
      } else {
        valA = a.monto_total_deuda || 0;
        valB = b.monto_total_deuda || 0;
      }
      return ordenDir === 'asc' ? valA - valB : valB - valA;
    });
    return lista;
  })();

  return (
    <div className="p-6" data-testid="morosidad-section">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-white">Morosidad</h1>
        <Button 
          onClick={handleExportExcel} 
          disabled={exportando || morosos.length === 0}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
          data-testid="export-morosidad-btn"
        >
          <Download className="w-4 h-4" />
          {exportando ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>

      {/* Filtros */}
      <Card className="bg-slate-800/50 border-slate-700 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Fecha de corte</label>
            <Input
              type="date"
              value={fechaCorte}
              onChange={e => setFechaCorte(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              data-testid="fecha-corte-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Categoria</label>
            <Select value={categoriaFiltro} onValueChange={setCategoriaFiltro}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="morosidad-cat-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas las categorias</SelectItem>
                {categorias.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Buscar jugador</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Nombre o RUT..."
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white pl-9"
                data-testid="morosidad-search"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <Users className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="total-morosos">{totalMorosos}</p>
              <p className="text-xs text-slate-400">Morosos</p>
            </div>
          </div>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <FileText className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="total-cuotas-impagas">{totalCuotas}</p>
              <p className="text-xs text-slate-400">Cuotas impagas</p>
            </div>
          </div>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <DollarSign className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="total-deuda-morosa">{formatMonto(totalDeuda)}</p>
              <p className="text-xs text-slate-400">Deuda total vencida</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabla de morosos */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : morososFiltrados.length === 0 ? (
        <Card className="bg-emerald-900/20 border-emerald-500/30 p-8 text-center">
          <p className="text-emerald-400 font-medium text-lg">Sin morosos</p>
          <p className="text-slate-400 text-sm mt-1">No hay integrantes con cuotas vencidas hasta el {formatFecha(fechaCorte)}</p>
        </Card>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-700/50">
                  <th className="p-3 text-left text-slate-400 w-8"></th>
                  <th className="p-3 text-left text-slate-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleOrden('nombre')}>
                    <span className="flex items-center gap-1">
                      Nombre
                      {ordenarPor === 'nombre' ? (ordenDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : null}
                    </span>
                  </th>
                  <th className="p-3 text-left text-slate-400 hidden sm:table-cell">RUT</th>
                  <th className="p-3 text-left text-slate-400 hidden md:table-cell">Categoria</th>
                  <th className="p-3 text-center text-slate-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleOrden('cuotas')}>
                    <span className="flex items-center justify-center gap-1">
                      Cuotas
                      {ordenarPor === 'cuotas' ? (ordenDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : null}
                    </span>
                  </th>
                  <th className="p-3 text-right text-slate-400 cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleOrden('monto')}>
                    <span className="flex items-center justify-end gap-1">
                      Deuda
                      {ordenarPor === 'monto' ? (ordenDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : null}
                    </span>
                  </th>
                  <th className="p-3 text-center text-slate-400">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {morososFiltrados.map(m => (
                  <React.Fragment key={m.integrante_id}>
                    <tr className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer" onClick={() => toggleRow(m.integrante_id)}>
                      <td className="p-3">
                        {expandedRows[m.integrante_id] 
                          ? <ChevronDown className="w-4 h-4 text-slate-400" /> 
                          : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      </td>
                      <td className="p-3 text-white font-medium">
                        {m.integrante?.apellido}, {m.integrante?.nombre}
                      </td>
                      <td className="p-3 text-slate-300 font-mono text-xs hidden sm:table-cell">
                        {m.integrante?.rut || '-'}
                      </td>
                      <td className="p-3 hidden md:table-cell">
                        {(m.categorias_nombres || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {m.categorias_nombres.map((cat, i) => (
                              <Badge key={i} className="bg-blue-500/20 text-blue-300 text-xs">{cat}</Badge>
                            ))}
                          </div>
                        ) : (
                          <Badge className="bg-amber-500/20 text-amber-400 text-xs">Sin categoria</Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <Badge className="bg-red-500/20 text-red-400">{m.cuotas_impagas}</Badge>
                      </td>
                      <td className="p-3 text-right text-red-400 font-semibold">
                        {formatMonto(m.monto_total_deuda)}
                      </td>
                      <td className="p-3 text-center">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-blue-600/50 text-blue-400 hover:bg-blue-600/20 h-7 text-xs gap-1"
                          onClick={(e) => { e.stopPropagation(); onVerCartola(m.integrante); }}
                          data-testid={`ver-cartola-${m.integrante_id}`}
                        >
                          <Eye className="w-3 h-3" /> Cartola
                        </Button>
                      </td>
                    </tr>
                    {expandedRows[m.integrante_id] && (
                      <tr>
                        <td colSpan="7" className="bg-slate-900/50 p-0">
                          <div className="px-6 py-3">
                            <p className="text-xs text-slate-400 mb-2 font-semibold">Detalle cuotas vencidas:</p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-slate-500">
                                  <th className="text-left pb-1">Descripcion</th>
                                  <th className="text-center pb-1">Vencimiento</th>
                                  <th className="text-right pb-1">Monto</th>
                                  <th className="text-right pb-1">Pagado</th>
                                  <th className="text-right pb-1">Deuda</th>
                                  <th className="text-center pb-1">Estado</th>
                                </tr>
                              </thead>
                              <tbody>
                                {m.cuotas_detalle.map((cuota, i) => (
                                  <tr key={cuota.id || i} className="border-t border-slate-700/30">
                                    <td className="py-1.5 text-slate-300">{cuota.descripcion || '-'}</td>
                                    <td className="py-1.5 text-center text-slate-400">{formatFecha(cuota.fecha_vencimiento)}</td>
                                    <td className="py-1.5 text-right text-slate-300">{formatMonto(cuota.monto_final)}</td>
                                    <td className="py-1.5 text-right text-emerald-400">{formatMonto(cuota.monto_pagado)}</td>
                                    <td className="py-1.5 text-right text-red-400 font-semibold">{formatMonto(cuota.deuda)}</td>
                                    <td className="py-1.5 text-center">
                                      <Badge className={`text-xs ${cuota.estado === 'vencida' ? 'bg-red-500/20 text-red-400' : cuota.estado === 'parcial' ? 'bg-amber-500/20 text-amber-400' : 'bg-slate-500/20 text-slate-400'}`}>
                                        {cuota.estado}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
